/**
 * Manages auto-scroll for the chat viewport: scroll tracking (show "scroll to
 * bottom" when user scrolls up), auto-scroll on new content when near bottom,
 * and a stable scrollToBottom callback for the button.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

const SCROLL_BUTTON_THRESHOLD = 120;
const AUTO_SCROLL_THRESHOLD = 200;

const scrollIntoViewEnd = (element: HTMLElement, behavior: ScrollBehavior) => {
  element.scrollIntoView({
    behavior,
    block: "end",
    inline: "nearest",
  });
};

export interface UseChatScrollDeps {
  isStreaming: boolean;
  messageCount: number;
  /** When set, opening/switching this thread scrolls to the latest messages. */
  conversationId?: string;
  /**
   * When false, the scroll viewport is not mounted (e.g. empty chat). Listeners
   * must attach only after this becomes true or they never run.
   */
  isScrollAreaMounted: boolean;
  /** True from a dropped stream until the resumed stream delivers its first chunk. */
  isStreamConnectionLost?: boolean;
  /** Text length of the latest assistant message; ends the stream reconnect scroll hold. */
  latestAssistantTextLength?: number;
}

interface StreamReconnectScrollHold {
  messageListHeight: number;
  latestAssistantTextLength: number;
}

export interface UseChatScrollReturn {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  endRef: React.RefObject<HTMLDivElement | null>;
  showScrollButton: boolean;
  scrollToBottom: () => void;
  /** Minimum message list height in px while a reconnected stream rebuilds the answer; undefined otherwise. */
  messageListMinHeight: number | undefined;
}

const useChatScroll = ({
  isStreaming,
  messageCount,
  conversationId,
  isScrollAreaMounted,
  isStreamConnectionLost = false,
  latestAssistantTextLength = 0,
}: UseChatScrollDeps): UseChatScrollReturn => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [streamReconnectScrollHold, setStreamReconnectScrollHold] =
    useState<StreamReconnectScrollHold | null>(null);
  const latestAssistantTextLengthRef = useRef(latestAssistantTextLength);
  latestAssistantTextLengthRef.current = latestAssistantTextLength;

  const scrollToBottom = useCallback(() => {
    const anchor = endRef.current;
    if (anchor) {
      scrollIntoViewEnd(anchor, "smooth");
    }
  }, []);

  useEffect(() => {
    if (!isScrollAreaMounted) return;

    const root = scrollRef.current;
    const target = endRef.current;
    if (!root || !target) return;

    const syncFromMetrics = () => {
      const raw = root.scrollHeight - root.scrollTop - root.clientHeight;
      const d = Math.max(0, raw);
      setShowScrollButton(d > SCROLL_BUTTON_THRESHOLD);
    };

    const onIntersect: IntersectionObserverCallback = (entries) => {
      const entry = entries[0];
      if (!entry) return;
      if (entry.isIntersecting) {
        setShowScrollButton(false);
        return;
      }
      syncFromMetrics();
    };

    let observer: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(onIntersect, {
        root,
        rootMargin: "0px",
        threshold: [0, 1],
      });
      observer.observe(target);
    }

    const scrollOpts = { passive: true } as const;
    root.addEventListener("scroll", syncFromMetrics, scrollOpts);
    root.addEventListener("scrollend", syncFromMetrics, scrollOpts);

    syncFromMetrics();

    return () => {
      observer?.disconnect();
      root.removeEventListener("scroll", syncFromMetrics);
      root.removeEventListener("scrollend", syncFromMetrics);
    };
  }, [isScrollAreaMounted, conversationId]);

  // `messageCount` omitted from deps: re-pin only when the thread or scroll mount changes.
  // If it were included, every new message would scroll to the end (bad while reading up-thread).
  useLayoutEffect(() => {
    if (conversationId === undefined || !isScrollAreaMounted) return;
    const el = scrollRef.current;
    const anchor = endRef.current;
    if (!el || !anchor || messageCount === 0) return;

    scrollIntoViewEnd(anchor, "auto");
    const raw = el.scrollHeight - el.scrollTop - el.clientHeight;
    const distanceFromBottom = Math.max(0, raw);
    setShowScrollButton(distanceFromBottom > SCROLL_BUTTON_THRESHOLD);
  }, [conversationId, isScrollAreaMounted]);

  useEffect(() => {
    if (!isScrollAreaMounted) return;
    const el = scrollRef.current;
    if (!el) return;

    const distanceFromBottom = Math.max(
      0,
      el.scrollHeight - el.scrollTop - el.clientHeight,
    );
    if (distanceFromBottom < AUTO_SCROLL_THRESHOLD) {
      const anchor = endRef.current;
      if (anchor) {
        scrollIntoViewEnd(anchor, "auto");
      }
    }

    const id = requestAnimationFrame(() => {
      const raw = el.scrollHeight - el.scrollTop - el.clientHeight;
      const d = Math.max(0, raw);
      setShowScrollButton(d > SCROLL_BUTTON_THRESHOLD);
    });

    return () => cancelAnimationFrame(id);
  }, [messageCount, isStreaming, isScrollAreaMounted]);

  // On resume the AI SDK rebuilds the answer from empty. Holding the height stops the browser
  // from clamping scrollTop to 0, so the view stays where it was (at the bottom if the user was there).
  useLayoutEffect(() => {
    if (!isStreamConnectionLost) return;
    const el = scrollRef.current;
    if (!el) return;
    // The list fills the scroll content box, so its height is scrollHeight without the vertical padding.
    const { paddingTop, paddingBottom } = getComputedStyle(el);
    setStreamReconnectScrollHold(
      (currentHold) =>
        currentHold ?? {
          messageListHeight:
            el.scrollHeight -
            Number.parseFloat(paddingTop) -
            Number.parseFloat(paddingBottom),
          latestAssistantTextLength: latestAssistantTextLengthRef.current,
        },
    );
  }, [isStreamConnectionLost]);

  useEffect(() => {
    if (streamReconnectScrollHold == null || isStreamConnectionLost) return;
    const isAnswerStillRebuilding =
      isStreaming &&
      latestAssistantTextLength <
        streamReconnectScrollHold.latestAssistantTextLength;
    if (isAnswerStillRebuilding) return;
    setStreamReconnectScrollHold(null);
  }, [
    streamReconnectScrollHold,
    isStreamConnectionLost,
    isStreaming,
    latestAssistantTextLength,
  ]);

  return {
    scrollRef,
    endRef,
    showScrollButton,
    scrollToBottom,
    messageListMinHeight: streamReconnectScrollHold?.messageListHeight,
  };
};

export default useChatScroll;
